import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import IncompleteProfileModal from '@/desktop/order.loyalty/datacandy/incomplete-profile-modal';
import MultipleCardsModal from '@/desktop/order.loyalty/datacandy/multiple-cards-modal';

const { Customer, UI } = Mobi2Go;
const { requestAddCardToProfile } = Customer.DataCandyLoyalty;

const Datacandy = ({}) => {
    const [t] = useTranslation('order.loyalty');
    const [isDataCandyEnabled, setIsDataCandyEnabled] = useState(false);
    const [customerEmail, setCustomerEmail] = useState('');
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [hideCardInputField, setHideCardInputField] = useState(false);
    const [savedCards, setSavedCards] = useState([]);
    const [chosenCard, setChosenCard] = useState(null);
    const [cardNumberInput, setCardNumberInput] = useState('');
    const [cardCharacterCount, setCardCharacterCount] = useState(0);
    const [incorrectCardNumber, setIncorrectCardNumber] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);

    const incompleteProfileModal = () => {
        const modal = UI.ExternalModal.create(
            'incomplete-profile',
            <IncompleteProfileModal closeModal={() => modal.close()} />
        );

        modal.mount();
        modal.open();
    };

    const multipleCardsModal = () => {
        const modal = UI.ExternalModal.create(
            'multiple-cards',
            <MultipleCardsModal
                closeModal={() => modal.close()}
                setModalIsClosable={isCloseable =>
                    modal.setIsClosable(isCloseable)
                }
                savedCards={savedCards}
                chosenCard={chosenCard}
                setChosenCard={setChosenCard}
                customerEmail={customerEmail}
            />
        );

        modal.mount();
        modal.open();
    };

    const updateState = () => {
        const loyalty = Customer.getLoyalty('DataCandyLoyalty');

        setIsDataCandyEnabled(!!loyalty);
        setChosenCard(
            loyalty?.state?.chosen_card !== null
                ? loyalty?.state?.chosen_card
                : null
        );
        setSavedCards(
            loyalty?.state?.linked_cards !== null
                ? loyalty?.state?.linked_cards
                : []
        );
        setHideCardInputField(savedCards.length > 0);
        setCustomerEmail(Customer.getEmail());
        setIsLoggedIn(Customer.isLoggedIn());
    };

    useEffect(() => {
        updateState();
        Customer.bind({
            logIn: updateState,
            logOut: updateState,
            updateLoyalty: updateState,
        });

        return () => {
            Customer.unbind({
                logIn: updateState,
                logOut: updateState,
                updateLoyalty: updateState,
            });
        };
    }, []);

    useEffect(() => {
        if (isLoggedIn && savedCards?.length > 1 && !chosenCard) {
            _.delay(() => {
                multipleCardsModal();
            }, 1250);
        }
    }, [isLoggedIn]);

    useEffect(() => {
        if (savedCards?.length > 0 || chosenCard) {
            setHideCardInputField(true);
        }
    }, [savedCards, chosenCard]);

    useEffect(() => {
        if (cardCharacterCount && cardCharacterCount < 13 || cardCharacterCount > 21) {
            setIncorrectCardNumber(true);
        } else {
            setIncorrectCardNumber(false);
        }
    }, [cardCharacterCount]);

    const handleAddCard = async () => {
        setIsRefreshing(true);

        try {
            const profileComplete = await requestAddCardToProfile(
                customerEmail,
                cardNumberInput
            );
            setChosenCard(cardNumberInput);
            setCardNumberInput('');
            Customer.trigger('updateLoyalty');

            if (!profileComplete) {
                incompleteProfileModal();
            }
        } catch (err) {
            throw err;
        } finally {
            setIsRefreshing(false);
        }
    };

    const showErrorMessage = (message, title = 'Error') => {
        Mobi2Go.UI.centeredAlert({
            title: title,
            body: message,
            temp: true,
            temp_duration: 10000,
        });
    };

    const onAddCardSubmit = () => {
        if (!incorrectCardNumber && cardCharacterCount !== 0) {
            handleAddCard().catch(() =>
                showErrorMessage('Something went wrong adding card to profile')
            );
        }
    };

    const onAddCardInputChange = e => {
        setCardNumberInput(e.target.value);
        setCardCharacterCount(e.target.value.length);
    };

    const onAddCardKeyDown = e => {
        const ENTER_KEY = 13;
        if (e.keyCode === ENTER_KEY) {
            onAddCardSubmit();
        }
    };

    const className = name => `m2g-customer-loyalty-datacandy${name || ''}`;

    if (!isLoggedIn || !isDataCandyEnabled) {
        return null;
    }

    return (
        <>
            {chosenCard ? (
                <div className={className('-stored-cards-container')}>
                    <div className={className('-stored-cards')}>
                        <div className="m2g-icon m2g-icon--credit-card"></div>
                        <span className={className('-stored-cards-numbers')}>
                            {chosenCard}
                        </span>
                        {savedCards?.length > 1 && (
                            <button
                                className={className('-switch-button')}
                                onClick={multipleCardsModal}
                            >
                                <div
                                    className={className(
                                        '-switch-icon m2g-icon m2g-icon--switch'
                                    )}
                                ></div>
                                <span
                                    aria-label={t('switch_label')}
                                    className={className('-switch-label')}
                                >
                                    {t('switch_label')}
                                </span>
                            </button>
                        )}
                    </div>
                </div>
            )
            : ( // If no chosen card display enticement
                <div
                className={className('-enticement-container')}
                >
                <p
                className={className('-enticement-text')}
                >
                    {t('customer:loyalty_opt_in_question')}
                    <a
                        className={className('-enticement-text-link')}
                        onClick={() => UI.trigger('showPolicy', 'loyalty')}
                    >
                        {t('customer:loyalty_scheme_name')}
                    </a>
                </p>
                </div>
                )
            }

            {!hideCardInputField && (
                <>
                    <div className={className()}>
                        <div className="m2g-icon m2g-icon--credit-card"></div>
                        <div className={className('-add-card-number')}>
                            <div
                                className={className(
                                    '-add-card-number-input m2g-list-cell'
                                )}
                            >
                                <input
                                    type="number"
                                    onKeyDown={onAddCardKeyDown}
                                    onChange={onAddCardInputChange}
                                    value={cardNumberInput}
                                    className={className(
                                        '-add-card-number-input m2g-list-cell__input m2g-list-cell__truncate m2g-list-cell__truncate--for-button'
                                    )}
                                    placeholder={t('membership_card')}
                                />
                            </div>
                        </div>

                        {isRefreshing && (
                            <div
                                className={className(
                                    '-loading-indicator-container'
                                )}
                            >
                                <div
                                    className={className(
                                        '-loading-indicator m2g-icon m2g-icon--spinner m2g-icon--small'
                                    )}
                                />
                            </div>
                        )}
                    </div>

                    {incorrectCardNumber && (
                        <p className={className('-card-warning')}>
                            {t('card_number_invalid')}
                        </p>
                    )}

                    <button
                        className={className(
                            '-add-card-button m2g-button m2g-button--primary m2g-button--add-button'
                        )}
                        onClick={onAddCardSubmit}
                    >
                        {t('add_card')}
                    </button>
                </>
            )}
        </>
    );
};

export default Datacandy;
